import {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Box,
  Stack,
  Popover,
  useTheme,
  InputBase,
  Tooltip,
  Paper,
  IconButton,
  Chip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import { HotTable } from "@handsontable/react";
import { registerAllModules } from "handsontable/registry";
import IconTransform_ from "../iconsElement";
import type { TableRow } from "./types";
import { validateRow } from "./validation";
import type { ValidNamesLookup } from "./validation";
import {
  createTextErrorRenderer,
  createIconRenderer,
  createTagsRenderer,
  buildIconImageCache,
} from "./renderers";
import { buildTableStyles } from "./styles";
import {
  normalizeType,
  defaultIconNameForType,
  fillRowDefaults,
} from "./helpers";

registerAllModules();

const IconTransform = IconTransform_ as any;

export interface ElementsTableHandle {
  // Lê os dados atuais diretamente da instância do Handsontable.
  getRows: () => TableRow[];
}

export interface ElementsTableProps {
  mode: "add" | "edit";
  data: TableRow[];
  onDataChange: (rows: TableRow[]) => void;
  hostsCache: any[];
  popsCache: any[];
  mapsCache: any[];
  iconsElement: any[];
  validNamesLookup: ValidNamesLookup;
  onAddCustomIcon: () => void;
  // Tags existentes (GET /easyMaps/tags/findMany) e callback para registrar uma
  // tag nova no cache do pai, deixando-a pesquisável nas demais linhas.
  tagsCache: string[];
  onCreateTag: (tag: string) => void;
}

const ElementsTable = forwardRef<ElementsTableHandle, ElementsTableProps>(
  function ElementsTable(
    {
      mode,
      data,
      onDataChange,
      hostsCache,
      popsCache,
      mapsCache,
      iconsElement,
      validNamesLookup,
      onAddCustomIcon,
      tagsCache,
      onCreateTag,
    },
    ref,
  ) {
    const theme = useTheme();
    const hotRef = useRef<any>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    // Altura em pixels do container — repassada ao Handsontable. Percentuais
    // não atravessam o div interno do HotTable, então medimos o container.
    const [tableHeight, setTableHeight] = useState(0);

    useImperativeHandle(ref, () => ({
      getRows: () =>
        (hotRef.current?.hotInstance?.getSourceData() as TableRow[]) || [],
    }));

    // Mede o container e mantém a altura do grid sincronizada (responsivo).
    useEffect(() => {
      const el = wrapperRef.current;
      if (!el) return;
      const update = () => setTableHeight(el.clientHeight);
      update();
      const ro = new ResizeObserver(update);
      ro.observe(el);
      return () => ro.disconnect();
    }, []);

    // Popover de seleção de ícone
    const [iconSearch, setIconSearch] = useState("");
    const [iconPopover, setIconPopover] = useState<{
      anchorEl: HTMLElement | null;
      row: number;
    } | null>(null);

    // Refs para acesso estável dentro de callbacks com deps vazias
    const iconsElementRef = useRef(iconsElement);
    useEffect(() => {
      iconsElementRef.current = iconsElement;
    }, [iconsElement]);

    const popsCacheRef = useRef(popsCache);
    useEffect(() => {
      popsCacheRef.current = popsCache;
    }, [popsCache]);

    const mapsCacheRef = useRef(mapsCache);
    useEffect(() => {
      mapsCacheRef.current = mapsCache;
    }, [mapsCache]);

    const validNamesRef = useRef(validNamesLookup);
    useEffect(() => {
      validNamesRef.current = validNamesLookup;
    }, [validNamesLookup]);

    const onDataChangeRef = useRef(onDataChange);
    useEffect(() => {
      onDataChangeRef.current = onDataChange;
    }, [onDataChange]);

    const modeRef = useRef(mode);
    useEffect(() => {
      modeRef.current = mode;
    }, [mode]);

    const tagsCacheRef = useRef(tagsCache);
    useEffect(() => {
      tagsCacheRef.current = tagsCache;
    }, [tagsCache]);

    const onCreateTagRef = useRef(onCreateTag);
    useEffect(() => {
      onCreateTagRef.current = onCreateTag;
    }, [onCreateTag]);

    // Popover de seleção/criação de tags
    const [tagSearch, setTagSearch] = useState("");
    const [tagsPopover, setTagsPopover] = useState<{
      anchorEl: HTMLElement | null;
      row: number;
    } | null>(null);

    // Revalida todas as linhas (preenchendo padrões) e emite nova referência.
    const emitRevalidated = useCallback(() => {
      const hot = hotRef.current?.hotInstance;
      if (!hot) return;
      const allData = hot.getSourceData() as TableRow[];
      allData.forEach((row) => {
        fillRowDefaults(row, iconsElementRef.current);
        row._errors = validateRow(row, validNamesRef.current);
      });
      onDataChangeRef.current([...allData]);
    }, []);

    // Callback após qualquer alteração de célula.
    const handleAfterChange = useCallback(
      (changes: any[] | null, source: string) => {
        if (!changes || source === "loadData" || source === "internal_update")
          return;
        const hot = hotRef.current?.hotInstance;
        if (!hot) return;
        const isPaste = source === "CopyPaste.paste";
        const isFill = source === "Autofill.fill";
        const icons = iconsElementRef.current;

        changes.forEach(
          ([row, prop, oldValue, newValue]: [number, string, any, any]) => {
            if (oldValue === newValue) return;
            const rowData = hot.getSourceDataAtRow(row) as TableRow;
            if (!rowData) return;

            if (prop === "type") {
              const normalized = normalizeType(newValue);
              rowData.type = normalized;
              // Limpa o mapRef antigo: ao trocar de tipo a referência anterior
              // (ou ausência dela, no caso de Host) não vale mais. Sem isso, um
              // Host convertido em POP manteria um mapRefId inválido.
              rowData.mapRefId = null;
              if (!isPaste && !isFill) {
                hot.setDataAtRowProp(
                  row,
                  "selectedName",
                  null,
                  "internal_update",
                );
                hot.setDataAtRowProp(row, "title", null, "internal_update");
                hot.setDataAtRowProp(row, "hostItems", [], "internal_update");
                hot.setDataAtRowProp(
                  row,
                  "iconId",
                  defaultIconNameForType(normalized, icons),
                  "internal_update",
                );
              }
            }

            if (prop === "selectedName" && newValue) {
              // Para Host, o título inicia igual ao nome do elemento selecionado.
              if (rowData.type === "Host") {
                hot.setDataAtRowProp(row, "title", newValue, "internal_update");
              } else if (rowData.type === "POP") {
                const sel = popsCacheRef.current.find(
                  (p: any) => p.titulo === newValue,
                );
                rowData.mapRefId = sel?.id ?? null;
              } else if (rowData.type === "Mapa") {
                const sel = mapsCacheRef.current.find(
                  (m: any) => m.titulo === newValue,
                );
                rowData.mapRefId = sel?.id ?? null;
              }
            }
          },
        );

        emitRevalidated();
      },
      [emitRevalidated],
    );

    const handleBeforePaste = useCallback((data: any[][], coords: any[]) => {
      // A coluna de checkbox está sempre na posição 0 (adição e edição).
      const hot = hotRef.current?.hotInstance;
      const sel = hot?.getSelectedRangeLast();
      const startCol = sel?.from?.col ?? coords?.[0]?.startCol ?? 0;
      if (startCol === 0 && Array.isArray(data)) {
        data.forEach((rowArr) => rowArr.unshift(""));
      }
    }, []);

    // Alterna a seleção de todas as linhas (adição e edição).
    const handleToggleSelectAll = useCallback(() => {
      const hot = hotRef.current?.hotInstance;
      if (!hot) return;
      const allData = hot.getSourceData() as TableRow[];
      const allChecked =
        allData.length > 0 && allData.every((row) => row.checked);
      allData.forEach((row) => {
        row.checked = !allChecked;
      });
      onDataChangeRef.current([...allData]);
    }, []);

    // Renderiza e sincroniza o checkbox "selecionar todos" no cabeçalho.
    const handleAfterGetColHeader = useCallback(
      (col: number, TH: HTMLElement) => {
        if (col !== 0 || !TH) return;
        const span =
          (TH.querySelector(".colHeader") as HTMLElement | null) || TH;
        let checkbox = span.querySelector(
          "input.ht-select-all",
        ) as HTMLInputElement | null;
        if (!checkbox) {
          span.textContent = "";
          checkbox = document.createElement("input");
          checkbox.type = "checkbox";
          checkbox.className = "ht-select-all";
          span.appendChild(checkbox);
        }
        const hot = hotRef.current?.hotInstance;
        const allData = (hot?.getSourceData() as TableRow[]) || [];
        const total = allData.length;
        const checked = allData.filter((r) => r.checked).length;
        checkbox.checked = total > 0 && checked === total;
        checkbox.indeterminate = checked > 0 && checked < total;
      },
      [],
    );

    // Intercepta cliques: checkbox do cabeçalho (linha -1) e célula de ícone
    const handleBeforeOnCellMouseDown = useCallback(
      (event: any, coords: any, TD: any) => {
        const hotInstance = hotRef.current?.hotInstance;
        if (!hotInstance) return;

        // Clique no cabeçalho da coluna 0 → alterna seleção (adição e edição)
        if (coords.row === -1) {
          if (coords.col === 0) {
            event.preventDefault();
            event.stopImmediatePropagation();
            handleToggleSelectAll();
          }
          return;
        }

        const prop = hotInstance.colToProp(coords.col);

        if (prop === "iconId" || prop === "tags") {
          const sel = hotInstance.getSelectedRangeLast();
          const alreadySelected =
            sel &&
            coords.row >= sel.from.row &&
            coords.row <= sel.to.row &&
            coords.col >= sel.from.col &&
            coords.col <= sel.to.col;
          if (!alreadySelected) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          if (prop === "iconId") {
            setIconPopover({ anchorEl: TD, row: coords.row });
            setIconSearch("");
          } else {
            setTagsPopover({ anchorEl: TD, row: coords.row });
            setTagSearch("");
          }
        }
      },
      [handleToggleSelectAll],
    );

    // Adiciona uma tag à linha (ignora duplicata case-insensitive). Usa um source
    // não ignorado pelo afterChange → dispara emitRevalidated e re-render da célula.
    const addTagToRow = useCallback((row: number, tag: string) => {
      const hot = hotRef.current?.hotInstance;
      if (!hot) return;
      const trimmed = tag.trim();
      if (!trimmed) return;
      const rowData = hot.getSourceDataAtRow(row) as TableRow;
      const current = Array.isArray(rowData?.tags) ? rowData.tags : [];
      if (current.some((t) => t.toLowerCase() === trimmed.toLowerCase())) return;
      hot.setDataAtRowProp(row, "tags", [...current, trimmed], "tagsEditor");
    }, []);

    const removeTagFromRow = useCallback((row: number, tag: string) => {
      const hot = hotRef.current?.hotInstance;
      if (!hot) return;
      const rowData = hot.getSourceDataAtRow(row) as TableRow;
      const current = Array.isArray(rowData?.tags) ? rowData.tags : [];
      hot.setDataAtRowProp(
        row,
        "tags",
        current.filter((t) => t !== tag),
        "tagsEditor",
      );
    }, []);

    //  Renderers e configuração das colunas
    const textErrorRenderer = useMemo(() => createTextErrorRenderer(), []);

    const iconImageCache = useMemo(
      () => buildIconImageCache(iconsElement, theme.palette.color.icons),
      [iconsElement, theme.palette.color.icons],
    );

    const iconRenderer = useMemo(
      () => createIconRenderer(iconImageCache),
      [iconImageCache],
    );

    const tagsRenderer = useMemo(() => createTagsRenderer(), []);

    const columns = useMemo(() => {
      const cols: any[] = [];
      // Checkbox de seleção: adição (remover linhas não salvas) e edição
      // (exclusão em massa de elementos persistidos).
      cols.push({
        data: "checked",
        title: "",
        type: "checkbox",
        width: 22,
        className: "htCenter htMiddle",
      });
      cols.push(
        {
          data: "type",
          title: "Tipo",
          type: "dropdown",
          source: ["Host", "POP", "Mapa"],
          allowInvalid: true,
          width: 70,
          renderer: textErrorRenderer,
        },
        {
          data: "selectedName",
          title: "Elemento",
          type: "autocomplete",
          strict: false,
          filter: true,
          allowInvalid: true,
          width: 145,
          renderer: textErrorRenderer,
        },
        {
          data: "title",
          title: "Título",
          type: "text",
          width: 140,
          renderer: textErrorRenderer,
        },
        {
          data: "iconId",
          title: "Ícone",
          readOnly: false,
          width: 40,
          renderer: iconRenderer,
        },
        {
          data: "lat",
          title: "Latitude",
          type: "text",
          width: 50,
          allowInvalid: true,
          renderer: textErrorRenderer,
        },
        {
          data: "long",
          title: "Longitude",
          type: "text",
          width: 50,
          allowInvalid: true,
          renderer: textErrorRenderer,
        },
        {
          data: "tags",
          title: "Tags",
          width: 150,
          editor: false,
          renderer: tagsRenderer,
        },
        {
          data: "description",
          title: "Descrição",
          type: "text",
          width: 140,
          renderer: textErrorRenderer,
        },
      );
      return cols;
    }, [mode, textErrorRenderer, iconRenderer, tagsRenderer]);

    // Fontes do autocomplete pré-computadas
    const hostNames = useMemo(
      () => hostsCache.map((h: any) => h.host),
      [hostsCache],
    );
    const popTitles = useMemo(
      () => popsCache.map((p: any) => p.titulo),
      [popsCache],
    );
    const mapTitles = useMemo(
      () => mapsCache.map((m: any) => m.titulo),
      [mapsCache],
    );

    // Configuração das células: fonte do autocomplete e bloqueio do título
    const cellsConfig = useCallback(
      (row: number, _col: number, prop: string | number) => {
        const cellProperties: any = {};
        const hotInstance = hotRef.current?.hotInstance;
        if (!hotInstance) return cellProperties;
        const rowData = hotInstance.getSourceDataAtRow(row) as TableRow;
        if (!rowData) return cellProperties;

        if (prop === "selectedName") {
          if (rowData.type === "Host") cellProperties.source = hostNames;
          else if (rowData.type === "POP") cellProperties.source = popTitles;
          else if (rowData.type === "Mapa") cellProperties.source = mapTitles;
          else cellProperties.source = [];
        }

        if (prop === "title") {
          cellProperties.readOnly = rowData.type !== "Host";
          if (rowData.type !== "Host") {
            cellProperties.className = "disabled-cell";
          }
        }

        return cellProperties;
      },
      [hostNames, popTitles, mapTitles],
    );

    const customStyles = useMemo(() => buildTableStyles(theme), [theme]);

    useEffect(() => {
      const hot = hotRef.current?.hotInstance;
      if (!hot) return;
      const rafId = requestAnimationFrame(() => {
        if (!hot.isDestroyed) hot.render();
      });
      return () => cancelAnimationFrame(rafId);
    }, []);

    const currentIconRowData = iconPopover
      ? (hotRef.current?.hotInstance?.getSourceDataAtRow(
          iconPopover.row,
        ) as TableRow)
      : null;

    const currentTagsRowData = tagsPopover
      ? (hotRef.current?.hotInstance?.getSourceDataAtRow(
          tagsPopover.row,
        ) as TableRow)
      : null;

    const currentTags = Array.isArray(currentTagsRowData?.tags)
      ? currentTagsRowData!.tags
      : [];
    const tagSearchTrimmed = tagSearch.trim();
    const tagSearchLower = tagSearchTrimmed.toLowerCase();
    // Sugestões: tags do cache que casam com a busca (case-insensitive).
    const filteredTagSuggestions = tagsCache.filter((t) =>
      t.toLowerCase().includes(tagSearchLower),
    );
    // Mostra "criar" quando o texto digitado não casa exatamente com nenhuma
    // tag conhecida nem com uma já aplicada na linha.
    const canCreateTag =
      tagSearchTrimmed !== "" &&
      !tagsCache.some((t) => t.toLowerCase() === tagSearchLower) &&
      !currentTags.some((t) => t.toLowerCase() === tagSearchLower);

    const commitTagSearch = () => {
      if (tagsPopover == null || tagSearchTrimmed === "") return;
      addTagToRow(tagsPopover.row, tagSearchTrimmed);
      if (!tagsCache.some((t) => t.toLowerCase() === tagSearchLower)) {
        onCreateTag(tagSearchTrimmed);
      }
      setTagSearch("");
    };

    return (
      <>
        <style>{customStyles}</style>
        <Box
          ref={wrapperRef}
          className="custom-table-wrapper"
          sx={{ height: "100%", overflow: "hidden", width: "100%" }}
        >
          <HotTable
            ref={hotRef}
            data={data}
            licenseKey="non-commercial-and-evaluation"
            rowHeaders={false}
            width="100%"
            height={tableHeight || undefined}
            stretchH="all"
            cells={cellsConfig}
            columns={columns}
            afterChange={handleAfterChange}
            beforePaste={handleBeforePaste}
            beforeOnCellMouseDown={handleBeforeOnCellMouseDown}
            afterGetColHeader={handleAfterGetColHeader}
            manualColumnResize={true}
            rowHeights={32}
            className="custom-table-wrapper"
          />
        </Box>

        {/* ── Popover: Seleção de Ícone ── */}
        <Popover
          open={Boolean(iconPopover)}
          anchorEl={iconPopover?.anchorEl}
          onClose={() => setIconPopover(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: {
              sx: {
                p: 1.5,
                width: 320,
                maxHeight: 350,
                overflow: "auto",
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              },
            },
          }}
        >
          <Stack direction={"row"} gap={1} alignItems={"center"}>
            <Paper
              sx={{
                backgroundColor:
                  theme.palette.background.paperSelectedMaps ||
                  theme.palette.action.hover,
                p: "5px 10px",
                display: "flex",
                alignItems: "center",
                width: "100%",
                boxShadow: "none",
                borderRadius: "7px",
                gap: "8px",
              }}
            >
              <SearchIcon
                fontSize="small"
                sx={{ color: theme.palette.text.disabled }}
              />
              <InputBase
                placeholder="Pesquisar ícones..."
                sx={{ width: "100%", fontSize: 14 }}
                value={iconSearch}
                onChange={(e) => setIconSearch(e.target.value)}
              />
            </Paper>

            <Tooltip title="Adicionar ícone customizado">
              <IconButton
                onClick={onAddCustomIcon}
                size="small"
                sx={{
                  backgroundColor:
                    theme.palette.background.paperSelectedMaps ||
                    theme.palette.action.hover,
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(5, 1fr)",
              gap: 1,
            }}
          >
            {iconsElement
              .filter((icon: any) =>
                (icon.nome || "")
                  .toLowerCase()
                  .includes(iconSearch.toLowerCase()),
              )
              .map((icon: any) => (
                <Tooltip key={icon.nome} title={icon.nome.replace("_", "")}>
                  <Box
                    onClick={() => {
                      const hotInstance = hotRef.current?.hotInstance;
                      if (hotInstance && iconPopover) {
                        hotInstance.setDataAtRowProp(
                          iconPopover.row,
                          "iconId",
                          icon.nome,
                          "iconPicker",
                        );
                      }
                      setIconPopover(null);
                    }}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 1,
                      borderRadius: "7px",
                      cursor: "pointer",
                      border:
                        currentIconRowData?.iconId === icon.nome
                          ? `2px solid ${theme.palette.primary.main}`
                          : `1px solid transparent`,
                      backgroundColor:
                        currentIconRowData?.iconId === icon.nome
                          ? theme.palette.action.selected
                          : "transparent",
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <IconTransform
                      img={icon?.img}
                      size="25"
                      color={theme.palette.color.icons}
                    />
                  </Box>
                </Tooltip>
              ))}
          </Box>
        </Popover>

        {/* ── Popover: Tags (pesquisar / adicionar / criar) ── */}
        <Popover
          open={Boolean(tagsPopover)}
          anchorEl={tagsPopover?.anchorEl}
          onClose={() => setTagsPopover(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: {
              sx: {
                p: 1.5,
                width: 300,
                maxHeight: 380,
                overflow: "hidden",
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              },
            },
          }}
        >
          <Paper
            sx={{
              backgroundColor:
                theme.palette.background.paperSelectedMaps ||
                theme.palette.action.hover,
              p: "5px 10px",
              display: "flex",
              alignItems: "center",
              width: "100%",
              boxShadow: "none",
              borderRadius: "7px",
              gap: "8px",
            }}
          >
            <SearchIcon
              fontSize="small"
              sx={{ color: theme.palette.text.disabled }}
            />
            <InputBase
              autoFocus
              placeholder="Pesquisar ou criar tag..."
              sx={{ width: "100%", fontSize: 14 }}
              value={tagSearch}
              onChange={(e) => setTagSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitTagSearch();
                }
              }}
            />
          </Paper>

          {/* Tags já aplicadas na linha */}
          {currentTags.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {currentTags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  onDelete={() =>
                    tagsPopover && removeTagFromRow(tagsPopover.row, tag)
                  }
                />
              ))}
            </Box>
          )}

          {/* Lista de tags existentes (clique alterna adicionar/remover) */}
          <Box sx={{ overflow: "auto", display: "flex", flexDirection: "column" }}>
            {filteredTagSuggestions.map((tag) => {
              const selected = currentTags.some(
                (t) => t.toLowerCase() === tag.toLowerCase(),
              );
              return (
                <Box
                  key={tag}
                  onClick={() => {
                    if (!tagsPopover) return;
                    if (selected) removeTagFromRow(tagsPopover.row, tag);
                    else addTagToRow(tagsPopover.row, tag);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    px: 1,
                    py: 0.75,
                    borderRadius: "7px",
                    cursor: "pointer",
                    backgroundColor: selected
                      ? theme.palette.action.selected
                      : "transparent",
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <Typography variant="body2" noWrap>
                    {tag}
                  </Typography>
                  {selected && (
                    <CheckIcon
                      fontSize="small"
                      sx={{ color: theme.palette.primary.main }}
                    />
                  )}
                </Box>
              );
            })}

            {canCreateTag && (
              <Box
                onClick={commitTagSearch}
                sx={{
                  px: 1,
                  py: 0.75,
                  borderRadius: "7px",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: theme.palette.action.hover },
                }}
              >
                <Typography variant="body2" color="text.secondary" noWrap>
                  Pressione Enter para criar a tag "{tagSearchTrimmed}"
                </Typography>
              </Box>
            )}

            {filteredTagSuggestions.length === 0 && !canCreateTag && (
              <Typography
                variant="body2"
                color="text.disabled"
                sx={{ px: 1, py: 0.75 }}
              >
                Nenhuma tag encontrada
              </Typography>
            )}
          </Box>
        </Popover>
      </>
    );
  },
);

export default ElementsTable;
